import { fold, left, right } from 'fp-ts/lib/Either';
import { compose } from 'lodash/fp';
import { always } from 'rambda';
import { useEffect, useState, useCallback } from 'react';
import { useSubmitRequestFlow } from '.';
import {
  IApiCallStatus,
  IError,
  IICAMToken,
  mapICAMTokenToUser,
  mapUserAuthenticated,
  refreshToken,
  useFeature,
  useQuery,
  useUserContext,
} from '../common';

export const useRefreshToken = () => {
  const { setUser } = useUserContext();
  const [refreshTokenStatus, set] = useState<IApiCallStatus>();

  const submitRequestFlow = useSubmitRequestFlow();
  let query = useQuery();

  useEffect(() => {
    const signin = compose(() => refreshToken().then(handleIcamSuccess));

    const run = compose(
      // left value is after okta signin redirect with code
      // refreshTokenStatus set to undefined
      // otherwise refreshTokenStatus is null, set by the one-time hook below

      fold<string, null, void>(() => set(void 0), signin),
      code => (code === null || submitRequestFlow ? right(code) : left('')),
      query.get.bind(query)
    );

    const handleIcamError = compose(() => {
      set(false);
    });

    const handleIcamSuccess = fold<IError, IICAMToken, void>(
      handleIcamError,
      compose(
        () => set(true),
        setUser,
        always(mapUserAuthenticated(true)),
        setUser,
        mapICAMTokenToUser
      )
    );

    refreshTokenStatus === null && run('code');
  }, [query, refreshTokenStatus, submitRequestFlow, setUser]);

  useFeature({ apiCallStatus: refreshTokenStatus });

  // reload should trigger refresh-token
  // also run after okta signin redirect
  useEffect(() => {
    set(null);
  }, []);

  useEffect(() => {
    setUser(a => ({
      ...a,
      refreshToken,
    }));
  }, [setUser]);

  const toRefreshToken = useCallback(() => {
    set(null);
  }, []);

  return {
    refreshTokenStatus,
    refreshToken: toRefreshToken,
  };
};
